<script setup>
import {defineExpose, defineProps, nextTick, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {isPositiveNumber} from '@shared/utils/helpers'
import {authenticationStore} from '@/main'
import {accessRules} from '@/permissions/accessControl'
import {useIsRouteActive} from '@shared/composables/router/useIsRouteActive'
import {findFirstAvailableRoute, findRouteByName} from '@shared/utils/router'

// Props
const props = defineProps({
  id: {
    type: String,
    default: 'sidebar-primary-dropdown',
  },
  data: {
    type: Object,
    required: true,
  },
  isMini: {
    type: [Boolean, Number],
    default: false,
  },
  isCounter: {
    type: Boolean,
    default: false,
  },
})

// Refs
const auth = authenticationStore
const router = useRouter()
const route = useRoute()
const showDropdownMenu = ref(false)
const visibleMenuLinks = ref([])

// Watchers
watch(
  () => route.path,
  async () => {
    await nextTick()
    const navDropdown = document.getElementById(props.id)
    if (!navDropdown) {
      return
    }

    visibleMenuLinks.value = navDropdown.querySelectorAll('.sidebar-primary-dropdown__item')
    showDropdownMenu.value = Array.from(visibleMenuLinks.value).some(link => link.classList.contains('active'))
  },
  {immediate: true},
)

// Methods
const toggleDropdownMenu = () => {
  showDropdownMenu.value = !showDropdownMenu.value
}

const closeDropdownMenu = () => {
  showDropdownMenu.value = false
}

const redirectToFirstAvailableRoute = async () => {
  const target = findFirstAvailableRoute(accessRules, props.data?.core?.parentRoute, auth, router).name
  await router.push({name: target})
}

const toggleDropdownAndRoute = async () => {
  if (!showDropdownMenu.value) {
    redirectToFirstAvailableRoute()
  }

  toggleDropdownMenu()
}

defineExpose({toggleDropdownMenu, closeDropdownMenu})
</script>

<template>
  <template v-if="isMini">
    <div
      v-if="data?.items?.length > 1"
      class="nav-item nav-item__mini"
      @mouseenter="showDropdownMenu = true"
      @mouseleave="showDropdownMenu = false"
    >
      <button
        @click="redirectToFirstAvailableRoute"
        class="nav-dropdown-button btn w-100 text-start sidebar-dropdown-router-link-button text-decoration-none"
        :class="{active: useIsRouteActive(props.data?.core?.parentRoute).value}"
        type="button"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-dropdown-button-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
              <span v-if="isCounter" class="nav-icon__notification-bullet" />
            </span>
          </span>
        </span>
      </button>

      <div v-show="showDropdownMenu" class="nav-item__menu">
        <ul class="nav-item__menu-ul">
          <template v-for="(item, index) in data?.items" :key="index">
            <li v-if="item.access">
              <router-link
                :to="{name: item.routeName}"
                :class="['nav-dropdown-menu-item', useIsRouteActive(item.routeName).value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-text">{{ item.title }}</span>

                <div v-if="isPositiveNumber(item.counter)" class="sidebar-count-badge custom mr-2">
                  {{ item.counter }}
                </div>
              </router-link>
            </li>
          </template>
        </ul>
      </div>
    </div>

    <div
      v-else-if="data?.items?.length === 1"
      class="nav-item nav-item__mini"
      v-tooltip.right="findRouteByName(data?.items[0].routeName, router)?.meta?.title"
    >
      <router-link
        :class="['nav-link', useIsRouteActive(data?.items[0].routeName).value ? 'active' : '']"
        :to="{name: data?.items[0].routeName}"
        active-class="active"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-link-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
              <span v-if="isCounter" class="nav-icon__notification-bullet" />
            </span>
          </span>
        </span>
      </router-link>
    </div>
  </template>

  <template v-else>
    <div
      v-if="data?.items?.length > 1"
      :id="id"
      class="sidebar-primary-dropdown"
      :class="{'sidebar-primary-dropdown--open': showDropdownMenu}"
    >
      <button
        @click="toggleDropdownAndRoute"
        type="button"
        class="sidebar-primary-dropdown__button btn w-100 d-flex align-items-center justify-content-between text-start text-decoration-none"
      >
        <span class="d-flex align-items-center custom gap-4 sidebar-primary-dropdown__label">
          <span class="sidebar-primary-dropdown__icon d-flex align-items-center justify-content-center">
            <slot name="button-icon" />
          </span>
          <span class="sidebar-primary-dropdown__title">{{ data?.mainButton?.title }}</span>
        </span>

        <span v-if="isPositiveNumber(data?.mainButton?.counter)" class="sidebar-count-badge custom mr-2">
          {{ data?.mainButton?.counter }}
        </span>

        <i
          :class="[
            'fa-light fa-chevron-up sidebar-primary-dropdown__chevron custom fs-5',
            !showDropdownMenu ? 'fa-rotate-180' : '',
          ]"
        />
      </button>

      <ul v-show="showDropdownMenu" class="sidebar-primary-dropdown__menu">
        <template v-for="(item, index) in data?.items" :key="index">
          <li v-if="item.access">
            <router-link
              :to="{name: item.routeName}"
              :class="[
                'sidebar-primary-dropdown__item d-flex align-items-center justify-content-between',
                useIsRouteActive(item.routeName).value ? 'active' : '',
              ]"
              active-class="active"
            >
              <span class="nav-text">{{ item.title }}</span>

              <div v-if="isPositiveNumber(item.counter)" class="sidebar-count-badge custom mr-2">
                {{ item.counter }}
              </div>
            </router-link>
          </li>
        </template>
      </ul>
    </div>

    <div v-else-if="data?.items?.length === 1" class="nav-item">
      <router-link
        :class="['nav-link', useIsRouteActive(data?.items[0].routeName).value ? 'active' : '']"
        :to="{name: data?.items[0].routeName}"
        active-class="active"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-link-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
            </span>
            <span class="nav-text">{{ findRouteByName(data?.items[0].routeName, router)?.meta?.title }}</span>
          </span>
          <span v-if="isPositiveNumber(data?.items[0].counter)" class="sidebar-count-badge custom mr-4">
            {{ data?.items[0].counter }}
          </span>
        </span>
      </router-link>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.sidebar-primary-dropdown {
  margin: 0 toRem(12) toRem(12);
  background: linear-gradient(169deg, #fff 0%, $primary-opacity 97%);
  border: toRem(0.5) solid #FBE2DB;
  border-radius: toRem(8);

  &--open {
    padding-bottom: toRem(14);
  }
}

.sidebar-primary-dropdown__button {
  background: transparent;
  border: toRem(1) solid transparent !important;
  border-radius: toRem(8);
  padding: toRem(14) toRem(12);
  transition: border-color 150ms ease;

  &:focus,
  &:active {
    border-color: transparent !important;
    box-shadow: none !important;
    background: transparent;
  }

  &:hover {
    border-color: #F7C6B7 !important;
  }
}

.sidebar-primary-dropdown__icon {
  color: $primary;
  width: toRem(20);
  font-size: toRem(20);
}

.sidebar-primary-dropdown__title {
  color: $black;
  font-weight: 600;
}

.sidebar-primary-dropdown__chevron {
  color: $text-color;
  transition: all linear 0.1s;
}

.sidebar-primary-dropdown__menu {
  list-style: none;
  margin: 0 toRem(12) 0 0;
  padding-left: toRem(20);
}

.sidebar-primary-dropdown__item {
  padding: toRem(10) toRem(25) toRem(10) toRem(24);
  border-radius: toRem(4);
  text-decoration: none;
  color: $text-color;
  transition: background-color 150ms ease, color 150ms ease;

  .nav-text {
    margin: 0;
  }

  &:hover {
    background: $secondary;
  }

  &.active {
    background: $secondary;
    color: $black;
    font-weight: 600;

    .nav-text {
      color: $black;
    }
  }
}
</style>
